import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { mergeStyleSets, getScreenSelector, ScreenWidthMinXXXLarge, Stack, StackItem, Link, ImageIcon, Text, Shimmer, ShimmerElementType, ScreenWidthMaxMedium, ScreenWidthMaxSmall } from '@fluentui/react';
import { AppContext } from '../../context/context.esm.js';
import Searchbar from '../../Searchbar/Searchbar.esm.js';
import { imageMap } from '../../../data/images.esm.js';
import { UNIFIED_SEARCHBAR_DROPDOWN, SEARCHBAR_DEFAULTS } from '../../Searchbar/constants.esm.js';
import { useBreakpoint, useHydration } from '@agoras/hooks';
import { useContext, useCallback } from 'react';
import AllButton from '../AllPanel/Header/AllButton/AllButton.esm.js';
import TopHeaderButtons from './Buttons/TopHeaderButtons.esm.js';
import { MS_LOGO_TEXT, MS_LOGO_LINK, UNIFIED_EXPERIENCE_MAIN_TITLE, APPSOURCE_MAIN_TITLE, HOME_PAGE_LOCATION_TYPE } from './constants.esm.js';
import { useHeaderButtons } from './Buttons/useHeaderButtons.esm.js';
import { useHeaderPanel } from '../../../hooks/useHeaderPanel.esm.js';
import UserContext from '../../context/userContext.esm.js';
import { useQuery } from '@tanstack/react-query';
import RestClient from '@agoras/rest-client';
import Separator from '../InlineSVG/Separator.esm.js';
import MSLogo from '../InlineSVG/MSLogo.esm.js';
import logger from '@agoras/logger';
import { headerContext } from '../context.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var _obj;
var classNames = mergeStyleSets({
    root: _define_property({
        height: '52px'
    }, getScreenSelector(ScreenWidthMinXXXLarge, undefined), {
        margin: '0 auto',
        width: ScreenWidthMinXXXLarge - 64
    }),
    link: {
        '&:hover': {
            textDecoration: 'none'
        },
        '&:active': {
            textDecoration: 'none'
        },
        '&:hover:active': {
            textDecoration: 'none'
        }
    },
    textTitle: {
        fontWeight: 600,
        lineHeight: '24px',
        cursor: 'pointer'
    },
    textLogoContainer: (_obj = {}, _define_property(_obj, "@media screen and (max-width: ".concat(ScreenWidthMaxMedium, "px)"), {
        marginRight: 'auto',
        marginLeft: '16px !important'
    }), _define_property(_obj, "@media screen and (max-width: ".concat(ScreenWidthMaxSmall, "px)"), {
        marginLeft: '8px !important'
    }), _obj),
    buttonsContainer: {
        height: '100%'
    }
});
var TopHeader = function(param) {
    var isAllPanelOpen = param.isAllPanelOpen, openAllPanel = param.openAllPanel, dismissAllPanel = param.dismissAllPanel, allButtonRef = param.allButtonRef;
    var _useContext = useContext(AppContext), searchProps = _useContext.searchProps, navigate = _useContext.navigate, unifiedExperience = _useContext.unifiedExperience;
    var _useContext1 = useContext(AppContext), signedIn = _useContext1.user.signedIn;
    var _useHeaderButtons = _sliced_to_array(useHeaderButtons(), 1), headerButtons = _useHeaderButtons[0];
    var _useHeaderPanel = useHeaderPanel(), openPanelKey = _useHeaderPanel.openPanelKey, setOpenPanelKey = _useHeaderPanel.setOpenPanelKey;
    var _useBreakpoint = _sliced_to_array(useBreakpoint('xxxl'), 1), isAboveXXXL = _useBreakpoint[0];
    var _useBreakpoint1 = _sliced_to_array(useBreakpoint('xl'), 1), isXL = _useBreakpoint1[0];
    var _useBreakpoint2 = _sliced_to_array(useBreakpoint('xl', 'xxxl'), 1), isXLAndHigher = _useBreakpoint2[0];
    var _useBreakpoint3 = _sliced_to_array(useBreakpoint('sm'), 1), isSmall = _useBreakpoint3[0];
    var isHydrated = useHydration();
    var agorasMainTitle = unifiedExperience ? UNIFIED_EXPERIENCE_MAIN_TITLE : APPSOURCE_MAIN_TITLE;
    var profileImageUrlQuery = useQuery({
        queryKey: [
            'user-profile-image'
        ],
        queryFn: function() {
            return RestClient.Graph.getProfileImage();
        },
        retry: 3,
        enabled: signedIn,
        staleTime: Infinity
    });
    var handleLogLink = useCallback(function(param) {
        var name = param.name, url = param.url;
        logger.info(JSON.stringify({
            name: name,
            url: url
        }), _object_spread({
            action: 'Navbar',
            actionModifier: 'Link'
        }, headerContext));
    }, []);
    var logoResponsiveMargins = {
        root: {
            marginRight: isAboveXXXL ? 'unset' : isXLAndHigher ? 'auto' : 'unset'
        }
    };
    var searchbarResponsiveMargins = {
        root: {
            ':nth-child(2)': isAboveXXXL ? {
                maxWidth: 772,
                margin: '0 auto'
            } : {
                maxWidth: isXLAndHigher ? isXL ? 432 : 600 : 'unset',
                margin: '0 auto'
            }
        }
    };
    return /*#__PURE__*/ jsxs(UserContext.Provider, {
        value: {
            profileImageUrl: profileImageUrlQuery.data
        },
        children: [
            /*#__PURE__*/ jsxs(Stack, {
                grow: true,
                horizontal: true,
                horizontalAlign: isXLAndHigher ? 'baseline' : 'space-between',
                verticalAlign: "center",
                className: classNames.root,
                children: [
                    !isXLAndHigher && /*#__PURE__*/ jsx(AllButton, {
                        renderText: false,
                        isOpen: isAllPanelOpen,
                        openPanel: openAllPanel,
                        dismissPanel: dismissAllPanel,
                        componentRef: allButtonRef
                    }),
                    /*#__PURE__*/ jsx(StackItem, {
                        styles: logoResponsiveMargins,
                        children: /*#__PURE__*/ jsxs(Stack, {
                            horizontal: true,
                            tokens: {
                                childrenGap: 10
                            },
                            verticalAlign: "center",
                            className: classNames.textLogoContainer,
                            children: [
                                !isSmall ? /*#__PURE__*/ jsxs(Fragment, {
                                    children: [
                                        /*#__PURE__*/ jsx(Link, {
                                            href: MS_LOGO_LINK,
                                            title: MS_LOGO_TEXT,
                                            onClick: function() {
                                                return handleLogLink({
                                                    name: MS_LOGO_TEXT,
                                                    url: MS_LOGO_LINK
                                                });
                                            },
                                            children: /*#__PURE__*/ jsx(Stack, {
                                                horizontal: true,
                                                verticalAlign: "center",
                                                children: /*#__PURE__*/ jsx(MSLogo, {})
                                            })
                                        }),
                                        /*#__PURE__*/ jsx(Separator, {})
                                    ]
                                }) : /*#__PURE__*/ jsx(Link, {
                                    href: MS_LOGO_LINK,
                                    children: /*#__PURE__*/ jsx(ImageIcon, {
                                        imageProps: {
                                            src: imageMap.MSlogoSmall,
                                            'aria-label': MS_LOGO_TEXT,
                                            alt: MS_LOGO_TEXT
                                        }
                                    })
                                }),
                                /*#__PURE__*/ jsx(Link, {
                                    onClick: function() {
                                        handleLogLink({
                                            name: agorasMainTitle,
                                            url: HOME_PAGE_LOCATION_TYPE
                                        });
                                        navigate({
                                            type: HOME_PAGE_LOCATION_TYPE
                                        });
                                    },
                                    className: classNames.link,
                                    children: /*#__PURE__*/ jsx(Text, {
                                        variant: "large",
                                        className: classNames.textTitle,
                                        children: agorasMainTitle
                                    })
                                })
                            ]
                        })
                    }),
                    isXLAndHigher && /*#__PURE__*/ jsxs(StackItem, {
                        styles: searchbarResponsiveMargins,
                        grow: true,
                        children: [
                            !searchProps.isSearchInHero && /*#__PURE__*/ jsx(Shimmer, {
                                isDataLoaded: isHydrated,
                                shimmerElements: [
                                    {
                                        type: ShimmerElementType.line,
                                        width: '100%',
                                        height: 32
                                    }
                                ]
                            }),
                            searchProps && !searchProps.isSearchInHero && isHydrated && /*#__PURE__*/ jsx(Searchbar, {
                                filters: searchProps.filters,
                                onSearch: searchProps.onSearch,
                                mfeContext: headerContext,
                                dropdownOptions: unifiedExperience ? UNIFIED_SEARCHBAR_DROPDOWN : SEARCHBAR_DEFAULTS.DROPDOWN
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(StackItem, {
                        className: classNames.buttonsContainer,
                        children: /*#__PURE__*/ jsx(TopHeaderButtons, {})
                    })
                ]
            }),
            headerButtons.filter(function(param) {
                var relatedPanel = param.relatedPanel;
                return !!relatedPanel;
            }).map(function(param) {
                var key = param.key, RelatedPanel = param.relatedPanel;
                return RelatedPanel ? /*#__PURE__*/ jsx(RelatedPanel, {
                    isOpen: openPanelKey === key,
                    dismissPanel: function() {
                        return setOpenPanelKey('');
                    }
                }, key) : null;
            })
        ]
    });
};

export { TopHeader as default };
